import {Typography} from '@mui/material';
import {useFormatters} from '@wallaby-ui/document';
import React, {useEffect, useCallback, useMemo} from 'react';
import {Modal} from 'react-bootstrap';

import {CombinationForm} from './dialog/CombinationForm';
import {CombinationList} from './dialog/CombinationList';
import {DialogFooter} from './dialog/DialogFooter';
import {ShortcutsEditor} from './dialog/ShortcutsEditor';
import {ShortcutsList} from './dialog/ShortcutsList';
import {useDialogState} from './dialog/useDialogState';
import {CombinationValueInput, CombinationInstance} from './useCombinations';

export type ShortcutData = {
  id?: number;
  combinationInstanceId: number;
  combinationInstanceName?: string;
  iconName?: string;
  position?: number;
};

type Props = {
  show: boolean;
  onClose: () => void;
  onCreate: (label: string, values: CombinationValueInput[]) => Promise<void>;
  onUpdate: (instanceId: number, label: string, values: CombinationValueInput[]) => Promise<void>;
  onDelete: (instanceId: number) => Promise<void>;
  drpParams: {
    drp_type: any;
    drp_causes: any;
    drp_plannedInterventions: any;
  };
  instances: CombinationInstance[];
  isLoadingInstances: boolean;
  refetchInstances: () => void;
  shortcuts: ShortcutData[];
  isLoadingShortcuts: boolean;
  onUpdateShortcuts: (shortcuts: ShortcutData[]) => Promise<void>;
};

const AVAILABLE_ICONS = [
  'circleOkIcon',
  'circleNokIcon',
  'circleExclamationIcon',
  'arrowDecreasingIcon',
  'arrowIncreasingIcon',
  'numberOneIcon',
  'numberTwoIcon',
  'numberThreeIcon',
  'numberFourIcon',
];

export const DrpCombinationManagementDialog: React.FC<Props> = ({
  show,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
  drpParams,
  instances,
  isLoadingInstances,
  refetchInstances,
  shortcuts,
  isLoadingShortcuts,
  onUpdateShortcuts,
}) => {
  const {t} = useFormatters();

  const {
    mode,
    setMode,
    editingInstanceId,
    label,
    setLabel,
    selectedType,
    setSelectedType,
    selectedCauses,
    setSelectedCauses,
    selectedInterventions,
    setSelectedInterventions,
    isSubmitting,
    setIsSubmitting,
    error,
    setError,
    editingShortcuts,
    setEditingShortcuts,
    isValid,
    isShortcutsValid,
    resetForm,
    loadInstanceForEdit,
    initializeShortcuts,
    updateShortcut,
    getCombinationValues,
  } = useDialogState();

  useEffect(() => {
    if (mode === 'edit-shortcuts') {
      initializeShortcuts(shortcuts);
    }
  }, [mode, shortcuts, initializeShortcuts]);

  const instanceOptions = useMemo(
    () =>
      instances.map((inst) => ({
        id: String(inst.id),
        label: t(inst.label || ''),
      })),
    [instances, t]
  );

  const handleSaveCombination = async () => {
    if (!isValid) {
      setError(t('hospitalization.detail.phRec.combination.validation.required'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const combinationValues = getCombinationValues();

      if (mode === 'edit' && editingInstanceId) {
        await onUpdate(editingInstanceId, label, combinationValues);
      } else {
        await onCreate(label, combinationValues);
      }

      resetForm();
      setMode('list');
      refetchInstances();
    } catch (err) {
      setError(t('hospitalization.detail.phRec.combination.create.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveShortcuts = async () => {
    if (!isShortcutsValid) {
      setError(t('hospitalization.detail.phRec.combination.shortcuts.validation.required'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onUpdateShortcuts(editingShortcuts);
      setMode('list');
    } catch (err) {
      setError(t('hospitalization.detail.phRec.combination.shortcuts.save.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = useCallback(
    async (instanceId: number) => {
      try {
        await onDelete(instanceId);
        refetchInstances();
      } catch (err) {
        console.error('Failed to delete combination:', err);
      }
    },
    [onDelete, refetchInstances]
  );

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      setMode('list');
      onClose();
    }
  };

  const handleBackToList = () => {
    resetForm();
    setEditingShortcuts([]);
    setError('');
    setMode('list');
  };

  const handleNewCombination = () => {
    resetForm();
    setMode('create');
  };

  const handleEditShortcuts = () => {
    setMode('edit-shortcuts');
  };

  const updateLabel = useCallback((newValue: string | null) => setLabel(newValue ?? ''), [setLabel]);

  const getDialogTitle = () => {
    if (mode === 'create') {
      return t('hospitalization.detail.phRec.combination.create.title');
    }
    if (mode === 'edit') {
      return t('hospitalization.detail.phRec.combination.edit.title');
    }
    if (mode === 'edit-shortcuts') {
      return t('hospitalization.detail.phRec.combination.shortcuts.edit.title');
    }
    return t('hospitalization.detail.phRec.combination.title');
  };

  return (
    <Modal show={show} onHide={handleClose} style={{borderRadius: 16}} className='dl-big-modal'>
      <div style={{maxHeight: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column'}}>
        <Modal.Header closeButton={!isSubmitting}>
          <Modal.Title>
            <Typography variant='labelBold' style={{fontSize: 18}}>
              {getDialogTitle()}
            </Typography>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{display: 'flex', flexDirection: 'column', gap: 16, flex: 1, minHeight: 0}}>
          {mode === 'list' ? (
            <>
              <CombinationList
                instances={instances}
                isLoading={isLoadingInstances || isLoadingShortcuts}
                shortcuts={shortcuts}
                onEdit={loadInstanceForEdit}
                onDelete={handleDelete}
                onNewCombination={handleNewCombination}
              />

              <ShortcutsList shortcuts={shortcuts} isLoading={isLoadingShortcuts} onEdit={handleEditShortcuts} />
            </>
          ) : mode === 'edit-shortcuts' ? (
            <ShortcutsEditor
              editingShortcuts={editingShortcuts}
              availableIcons={AVAILABLE_ICONS}
              instanceOptions={instanceOptions}
              onUpdateShortcut={updateShortcut}
              isSubmitting={isSubmitting}
              error={error}
            />
          ) : (
            <CombinationForm
              label={label}
              onLabelChange={updateLabel}
              selectedType={selectedType}
              onTypeChange={setSelectedType}
              selectedCauses={selectedCauses}
              onCausesChange={(value) => setSelectedCauses(value ?? [])}
              selectedInterventions={selectedInterventions}
              onInterventionsChange={(value) => setSelectedInterventions(value ?? [])}
              drpParams={drpParams}
              error={error}
              isSubmitting={isSubmitting}
            />
          )}
        </Modal.Body>

        <DialogFooter
          mode={mode}
          isValid={mode === 'edit-shortcuts' ? isShortcutsValid : isValid}
          isSubmitting={isSubmitting}
          onClose={handleClose}
          onBack={handleBackToList}
          onSave={handleSaveCombination}
          onSaveShortcuts={handleSaveShortcuts}
        />
      </div>
    </Modal>
  );
};
